import { action, computed, makeObservable, observable } from "mobx";
import { ICountryStore } from "../../../country/CountryStore";
import { IProductInfoStore } from "../../../product-info/ProductInfoStore";
import { CountryModelExtended } from "../../../country/CountryModelExtended";
import { isNullOrWhiteSpace } from "../../../../util/functions/isNullOrWhiteSpace";

export interface IAboutUsDialogStore {
	isOpen: boolean;
	showCountrySelector: boolean;
	selectedCountry: CountryModelExtended | null;
	customizedCountries: CountryModelExtended[];

	setSelectedCountry(selectedCountry: CountryModelExtended | null): void;
	setOpen(open: boolean): void;
}

export class AboutUsDialogStore implements IAboutUsDialogStore {
	@observable
	private _selectedCountry: CountryModelExtended | null | undefined = undefined;

	constructor(
		private readonly countryStore: ICountryStore,
		private readonly productInfoStore: IProductInfoStore,
	) {
		makeObservable(this);
	}

	@computed
	public get showCountrySelector(): boolean {
		return this.productInfoStore.isMedicalDevice && this.customizedCountries.length > 0;
	}

	@observable
	public isOpen = false;

	@action
	public setOpen(open): void {
		this.isOpen = open;
	}

	@computed
	get selectedCountry(): CountryModelExtended | null {
		if (!this.showCountrySelector) return null;

		//Set selected country

		// 1. by manually selecting / clearing one
		// If _selectedCountry is not undefined, it means that it was changed manually -> this ALWAYS has priority
		if (this._selectedCountry !== undefined) {
			return this._selectedCountry;
		}

		// 2. by the current country that has customizations
		if (this.countryStore.currentCountry && this.customizedCountries.find(s => s.id === this.countryStore.currentCountry!.id)) {
			return this.countryStore.currentCountry;
		}

		// 3. if the current country does not have customization -> default to system setting if that is defined and that has customizations
		if (this.countryStore.defaultCountryAboutBox && this.customizedCountries.find(s => s.id === this.countryStore.defaultCountryAboutBox!.id)) {
			return this.countryStore.defaultCountryAboutBox;
		}

		// 4. if there is no system setting defined -> default to nothing selected
		return null;
	}

	@computed
	public get customizedCountries(): CountryModelExtended[] {
		const { allCountries } = this.countryStore;
		return allCountries.filter(s => !isNullOrWhiteSpace(s.companyInfo) || !isNullOrWhiteSpace(s.productInfo) || !isNullOrWhiteSpace(s.documentationInfo));
	}

	@action
	setSelectedCountry(selectedCountry: CountryModelExtended | null): void {
		this._selectedCountry = selectedCountry;
	}
}
